import { SelfDto, userControllerGetSelf } from '@/generated/korza-api-client';
import { UnauthorizedError } from '@/lib/api-client';
import { useQuery } from '@tanstack/react-query';
import { createContext, PropsWithChildren, useContext, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { queryCache } from '@/lib/query-client';

const SelfContext = createContext<SelfDto | null>(null);

export function useSelf(): SelfDto {
  const context = useContext(SelfContext);

  if (context === null) {
    throw new Error();
  }

  return context;
}

export function useSelfNullable(): SelfDto | null {
  return useContext(SelfContext);
}

export function useSelfQuery() {
  return useQuery<SelfDto | null>({
    queryKey: ['self'],
    queryFn: async () => {
      try {
        const result = await userControllerGetSelf();
        if (result.response.status === 401) {
          throw new UnauthorizedError(result.response);
        }
        if (result.error || !result.data) {
          throw new Error('Failed to get self ' + result.error);
        }

        storeUser(result.data);

        return result.data;
      } catch (e) {
        if (e instanceof UnauthorizedError) {
          storeUser(null);
          return null;
        }

        throw e;
      }
    },
    initialData: getStoredUser(),
    retryDelay: 1000,
  });
}

export function SelfProvider({ children }: PropsWithChildren) {
  const { data } = useSelfQuery();

  return <SelfContext.Provider value={data}>{children}</SelfContext.Provider>;
}

export function SelfGuard({ children }: PropsWithChildren) {
  useAutoLogout();
  const self = useSelfNullable();

  if (self === null) {
    return <Navigate to={'/login'} />;
  }

  return children;
}

export function useAutoLogout() {
  const navigate = useNavigate();

  // Effect to log out if it detects any unauthorized error
  useEffect(() => {
    const unsubscribe = queryCache.subscribe((event) => {
      if (
        event.type === 'updated' &&
        event.action.type === 'error' &&
        event.action.error instanceof UnauthorizedError
      ) {
        navigate('/login');
        storeUser(null);
      }
    });

    return unsubscribe;
  }, [navigate]);
}

const LOCAL_STORAGE_KEY = 'self';

function storeUser(user: SelfDto | null) {
  if (user === null) {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  } else {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(user));
  }
}

import { z } from 'zod';

const selfSchema = z.object({
  id: z.string(),
  email: z.string(),
  chatwootIdentifierHash: z.string(),
  isBackofficeUser: z.boolean(),
});

function getStoredUser(): SelfDto | null {
  const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (stored === null) {
    return null;
  }

  try {
    const parsed = JSON.parse(stored);
    const result = selfSchema.safeParse(parsed);
    if (!result.success) {
      return null;
    }
    return result.data;
  } catch {
    return null;
  }
}
